/**
 * AttentionBell — sidebar bell + dropdown surfacing the workspace's
 * decision queue.
 *
 * Pulls open attention_items for the current workspace, shows a count
 * badge, and renders a dropdown with one-click resolve / defer /
 * dismiss actions.
 *
 * Auto-refreshes via tanstack-query when BrowserBus broadcasts a
 * data_changed event with entity_type='attention_items' (already wired
 * in app/hooks/useBrowserBus.ts to invalidate the session-kickoff key
 * — we re-use the same pattern with our own key).
 *
 * Mounts in the host app's sidebar header. The component is
 * framework-extractable: it only depends on tanstack-query + the
 * authed fetch convention.
 */

import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bell } from "lucide-react";

interface AttentionItem {
  id: string;
  type: string;
  subject_type: string | null;
  subject_id: string | null;
  state: "open" | "deferred" | "resolved" | "dismissed";
  defer_until: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

export interface AttentionBellProps {
  /** Workspace whose attention queue to surface. Pass null/undefined
   *  when no workspace is selected — the bell will render disabled. */
  workspaceId: string | null | undefined;
  /** Optional CSRF header builder. Falls back to no header when omitted
   *  (the framework doesn't ship a CSRF opinion). */
  csrfHeader?: () => Record<string, string>;
  /** Entity-type → URL hint. Used so the "view subject" link points at
   *  the right page. Host apps can override; defaults map to the
   *  conventional CongressMCP URLs. */
  entityHrefBuilder?: (subjectType: string | null, subjectId: string | null) => string | null;
}

const DEFAULT_TYPE_LABELS: Record<string, string> = {
  whip_math_champion_gap: "Champion gap",
  extraction_review: "AI signal review",
  cooling_relationship: "Cooling relationship",
  conflict: "Source conflict",
  ai_extracted_stance: "AI-extracted stance",
};

function defaultEntityHref(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  switch (type) {
    case "member": return `/app/members/${encodeURIComponent(id)}`;
    case "bill": return `/app/bills/${encodeURIComponent(id)}`;
    case "contact": return `/app/contacts/${encodeURIComponent(id)}`;
    case "account":
    case "organization": return `/app/accounts/${encodeURIComponent(id)}`;
    case "issue": return `/app/issues/${encodeURIComponent(id)}`;
    case "coalition": return `/app/coalitions/${encodeURIComponent(id)}`;
    case "signal": return null; // signals don't have a dedicated detail page yet
    default: return null;
  }
}

function relativeTime(iso: string): string {
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function AttentionBell({
  workspaceId,
  csrfHeader,
  entityHrefBuilder = defaultEntityHref,
}: AttentionBellProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const { data, isLoading } = useQuery<{ items: AttentionItem[]; count: number }>({
    queryKey: ["attention-bell", workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId!)}/attention?state=open&limit=20`,
        { credentials: "include" }
      );
      if (!res.ok) return { items: [], count: 0 };
      return res.json();
    },
    staleTime: 30_000,
  });

  const items = data?.items ?? [];
  const count = items.length;

  const callAction = async (
    itemId: string,
    action: "resolve" | "defer" | "dismiss",
    body?: Record<string, unknown>
  ) => {
    if (!workspaceId) return;
    try {
      await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/attention/${encodeURIComponent(itemId)}/${action}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrfHeader?.() ?? {}),
          },
          body: JSON.stringify(body ?? {}),
        }
      );
      queryClient.invalidateQueries({ queryKey: ["attention-bell", workspaceId] });
    } catch {
      /* swallow — UI will show the item still queued; user can retry */
    }
  };

  if (!workspaceId) return null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`relative w-7 h-7 rounded-md flex items-center justify-center hover:bg-stone-100 ${count > 0 ? "text-stone-900" : "text-stone-400"}`}
        title={count > 0 ? `${count} thing${count === 1 ? "" : "s"} need${count === 1 ? "s" : ""} your attention` : "No pending attention items"}
        aria-label="Attention queue"
      >
        <Bell size={16} strokeWidth={1.75} aria-hidden />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-danger text-white text-[10px] font-bold rounded-full ring-2 ring-white flex items-center justify-center tabular-nums">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 z-50 w-[340px] max-h-[480px] bg-white border border-stone-200 rounded-lg shadow-overlay flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b border-stone-100 flex items-center bg-stone-50/60">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 flex-1">
              Attention queue
            </div>
            <div className="text-[11px] text-stone-400 font-mono">{count}</div>
          </div>
          <div className="overflow-y-auto divide-y divide-stone-100">
            {isLoading && (
              <div className="px-3 py-3 text-xs italic text-stone-400">Loading…</div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="px-3 py-6 text-center">
                <div className="text-xs text-stone-500">All clear.</div>
                <div className="text-[11px] text-stone-400 mt-0.5">
                  Nothing is queued for your decision right now.
                </div>
              </div>
            )}
            {!isLoading &&
              items.map((item) => {
                const summary =
                  (item.payload?.summary as string | undefined) ??
                  DEFAULT_TYPE_LABELS[item.type] ??
                  item.type;
                const href = entityHrefBuilder(item.subject_type, item.subject_id);
                return (
                  <div key={item.id} className="px-3 py-2.5 hover:bg-stone-50 flex flex-col gap-1.5">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} strokeWidth={1.75} className="text-warn shrink-0 mt-1" aria-hidden />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                          {DEFAULT_TYPE_LABELS[item.type] ?? item.type}
                        </div>
                        <div className="text-sm text-stone-800 leading-snug line-clamp-3">
                          {summary}
                        </div>
                      </div>
                      <span className="text-[11px] text-stone-400 font-mono shrink-0 mt-0.5">
                        {relativeTime(item.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 ml-6">
                      {href && (
                        <a
                          href={href}
                          className="text-[11px] font-semibold text-action hover:bg-stone-100 px-1.5 py-0.5 rounded"
                          onClick={() => setOpen(false)}
                        >
                          View
                        </a>
                      )}
                      <button
                        onClick={() => callAction(item.id, "resolve")}
                        className="text-[11px] font-semibold text-success hover:bg-success-soft px-1.5 py-0.5 rounded"
                      >
                        Resolve
                      </button>
                      <button
                        onClick={() => callAction(item.id, "defer", { hours: 24 })}
                        className="text-[11px] font-semibold text-warn hover:bg-warn-soft px-1.5 py-0.5 rounded"
                        title="Defer 24 hours"
                      >
                        Snooze 1d
                      </button>
                      <button
                        onClick={() => callAction(item.id, "dismiss")}
                        className="text-[11px] font-semibold text-stone-500 hover:bg-stone-100 px-1.5 py-0.5 rounded ml-auto"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
